import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useNoIndex } from '../utils/jsonLd';

/**
 * 404-style page rendered on probing paths (old /admin, /admin/dashboard, etc)
 * AND on any unmatched React Router route.
 *
 * SEO: because this is a client-side SPA we cannot return an HTTP 404 status
 * code. Google was flagging these responses as "Soft 404" in Search Console
 * (indexable HTML, but no real content). The `useNoIndex` hook injects a
 * `<meta name="robots" content="noindex, follow">` so crawlers explicitly
 * skip these pages. We also set a matching document title so any surfaces
 * that already cached the page get a sensible display name.
 */
const NotFound = () => {
  useNoIndex();

  useEffect(() => {
    const previous = document.title;
    document.title = '404 - Page Not Found · Outlaw Media';
    return () => { document.title = previous; };
  }, []);

  return (
    <div className="min-h-screen bg-black text-gray-300 flex items-center justify-center px-4" data-testid="not-found-page">
      <div className="text-center">
        <p className="text-[11px] tracking-[0.4em] uppercase text-red-500/80 font-bold mb-3">
          404
        </p>
        <h1 className="text-3xl md:text-4xl font-black text-white mb-2">
          Page Not Found
        </h1>
        <p className="text-sm text-gray-500 mb-6">
          That URL doesn't lead anywhere here.
        </p>
        <Link
          to="/"
          data-testid="not-found-home-link"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-red-600 hover:bg-red-500 text-white font-black text-sm uppercase tracking-wider transition-colors"
        >
          Back to Home
        </Link>
      </div>
    </div>
  );
};

export default NotFound;
